import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import axios from 'axios';
import { api } from '../config';
import { getUserDivision, saveUserDivision } from '../utils/departments';
import { userService } from '../services/api';

interface UserInfo {
  id?: number;
  name: string;
  email: string;
  username: string;
  role?: 'Regular User' | 'Administrator' | string;
  is_active?: boolean;
  is_reviewer?: boolean;
}

interface UserContextType {
  user: UserInfo | null;
  isLoading: boolean;
  error: string | null;
  loadingMessage: string;
  retryLogin: () => void;
  logout: () => void;
  isAdmin: boolean;
  isReviewer: boolean;
  isDQReviewer: boolean;
  dqDivision: string | null;
  division: string;
  setDivision: (div: string) => void;
  divisionModalOpen: boolean;
  setDivisionModalOpen: (open: boolean) => void;
}

const UserContext = createContext<UserContextType>({
  user: null,
  isLoading: true,
  error: null,
  loadingMessage: 'Initializing...',
  retryLogin: () => {},
  logout: () => {},
  isAdmin: false,
  isReviewer: false,
  isDQReviewer: false,
  dqDivision: null,
  division: '',
  setDivision: () => {},
  divisionModalOpen: false,
  setDivisionModalOpen: () => {},
});

export const useUser = () => useContext(UserContext);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
  const [division, setDivisionState] = useState('');
  const [divisionModalOpen, setDivisionModalOpen] = useState(false);
  const [isDQReviewer, setIsDQReviewer] = useState(false);
  const [dqDivision, setDqDivision] = useState<string | null>(null);

  const setDivision = (div: string) => {
    if (user?.email) {
      saveUserDivision(user.email, div);
      // Save to DB async, silently
      userService.setDivision(user.email, div).catch(() => {});
    }
    setDivisionState(div);
    setDivisionModalOpen(false);
  };

  const retryLogin = async () => {
    setError(null);
    setIsLoading(true);
    setHasAttemptedLogin(false);
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      setHasAttemptedLogin(false); // Reset attempt flag for next login
      setUser(null);
      setError(null);
      await instance.logout({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Failed to logout');
      setIsLoading(false);
    }
  };

  const registerUserWithBackend = async (email: string, name: string): Promise<UserInfo | null> => {
    // Call the auto-create endpoint to register/fetch user
    try {
      setLoadingMessage('Connecting to backend...');
      console.log('[Backend] Fetching user info for:', email);
      const response = await axios.get(api(`/api/users/me/${encodeURIComponent(email.toLowerCase())}`));
      const backendUser = response.data;
      console.log('[Backend] User fetched/created:', backendUser);
      
      // Update display name from Azure AD
      if (name) {
        try {
          console.log('[Backend] Updating display name to:', name);
          await axios.put(api(`/api/users/me/${encodeURIComponent(email.toLowerCase())}/display-name?display_name=${encodeURIComponent(name)}`));
          console.log('[Backend] ✓ Display name updated');
        } catch (nameErr) {
          console.warn('[Backend] Failed to update display name:', nameErr);
        }
      }

      // Use display_name from backend if available, otherwise use the Azure AD name
      const displayName = backendUser.display_name || name || 'User';
      
      // Get user's role from backend (Advanced Reviewer, Reviewer, or Requestor)
      let isUserReviewer = false;
      let userRole = 'Requestor';
      try {
        setLoadingMessage('Checking permissions...');
        const roleResponse = await axios.get(api(`/api/users/role/${encodeURIComponent(email.toLowerCase())}`));
        userRole = (roleResponse.data.role || '').trim();
        isUserReviewer = userRole === 'Advanced Reviewer' || userRole === 'Reviewer';
        console.log('[Backend] ✓ Role:', userRole, '| isReviewer:', isUserReviewer);
      } catch (roleErr: any) {
        console.warn('[Backend] Role check failed, trying fallback:', roleErr.message);
        try {
          const reviewerResponse = await axios.get(api(`/api/users/check-reviewer/${encodeURIComponent(email.toLowerCase())}`));
          isUserReviewer = !!reviewerResponse.data.is_reviewer;
          if (isUserReviewer) userRole = 'Reviewer';
          console.log('[Backend] ✓ Fallback reviewer check:', isUserReviewer);
        } catch (fallbackErr) {
          console.warn('[Backend] Fallback also failed, defaulting to Requestor');
        }
      }
      return {
        name: displayName,
        email: email.toLowerCase(),
        username: email.toLowerCase(),
        role: userRole,
        id: backendUser.id,
        is_active: backendUser.is_active,
        is_reviewer: isUserReviewer,
      };
    } catch (err: any) {
      console.error('[Backend] Failed to register user with backend:', err);
      throw err;
    }
  };

  useEffect(() => {
    const initializeUser = async () => {
      console.log('%c[UserContext] Starting user initialization', 'color: orange; font-weight: bold');
      console.log('[UserContext] inProgress:', inProgress);
      console.log('[UserContext] accounts count:', accounts.length);
      console.log('[UserContext] hasAttemptedLogin:', hasAttemptedLogin);
      
      // If authentication is in progress, wait
      if (inProgress !== InteractionStatus.None) {
        setLoadingMessage('Checking authentication...');
        console.log('[UserContext] Authentication in progress, waiting...');
        return;
      }

      // Check if user is already logged in (has Azure AD accounts)
      if (accounts.length > 0) {
        const account = accounts[0];
        setLoadingMessage('Found existing session...');
        console.log('%c[UserContext] ✓ Azure AD Account found:', 'color: green; font-weight: bold', account.username);
        
        try {
          // Register with backend (auto-create if needed)
          const userData = await registerUserWithBackend(account.username || '', account.name || 'User');
          setLoadingMessage('Completing setup...');
          setUser(userData);
          setIsLoading(false);
          setError(null);
          console.log('[UserContext] User setup completed successfully');
        } catch (err: any) {
          // Even if backend registration fails, allow access with basic user info
          console.warn('[UserContext] Backend registration failed, using local user info:', err);
          setUser({
            name: account.name || 'User',
            email: account.username || '',
            username: account.username || '',
            role: 'Regular User',
            is_active: true,
            is_reviewer: false,
          });
          setLoadingMessage('Setup complete');
          setIsLoading(false);
          // Don't set error - allow user to proceed
        }
        return;
      }

      // No accounts found - user is not logged in or has logged out
      if (hasAttemptedLogin) {
        console.log('[UserContext] Already attempted login, waiting for Azure AD response...');
        console.log('[UserContext] If you do not see a login screen, please refresh the page');
        setLoadingMessage('Waiting for login... (If stuck, refresh the page)');
        return;
      }

      // Mark that we're attempting login
      setHasAttemptedLogin(true);
      console.log('%c[UserContext] No user found, need to login', 'color: yellow; font-weight: bold');

      try {
        // Try silent login first
        setLoadingMessage('Checking for existing session...');
        console.log('[UserContext] Attempting silent login...');
        
        const silentResponse = await instance.ssoSilent({
          ...loginRequest,
          loginHint: undefined,
        });
        
        if (silentResponse?.account) {
          console.log('%c[UserContext] ✓ Silent login successful!', 'color: green; font-weight: bold');
          const userData = await registerUserWithBackend(
            silentResponse.account.username || '', 
            silentResponse.account.name || 'User'
          );
          setUser(userData);
          setIsLoading(false);
          console.log('[UserContext] User logged in successfully');
          return;
        }
      } catch (silentErr) {
        console.log('[UserContext] Silent login not available, will use interactive login');
      }

      // Silent login failed - use interactive login
      setLoadingMessage('Starting login...');
      console.log('%c[UserContext] Starting interactive login redirect', 'color: cyan; font-weight: bold');
      
      try {
        // Check if instance is initialized and ready
        console.log('[UserContext] Calling instance.loginRedirect()...');
        await instance.loginRedirect(loginRequest);
        console.log('[UserContext] loginRedirect initiated - browser will redirect to Azure AD');
        setLoadingMessage('Redirecting to Azure AD...');
      } catch (redirectErr: any) {
        console.error('%c[UserContext] ✗ loginRedirect failed:', 'color: red; font-weight: bold', redirectErr);
        console.error('[UserContext] Error details:', redirectErr.message, redirectErr.code);
        
        // Check if error is due to popup blockers or config issues
        if (redirectErr.message?.includes('popup') || redirectErr.code?.includes('popup')) {
          setError('Your browser may be blocking popups. Please allow popups for this site and try again.');
        } else if (redirectErr.message?.includes('cookie') || redirectErr.message?.includes('storage')) {
          setError('Cookies or local storage may be disabled. Please enable them and try again.');
        } else {
          setError('Unable to start login. Please try refreshing the page. If the problem persists, clear your browser cache and try again.');
        }
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [inProgress, accounts, instance, hasAttemptedLogin]);

  // Load division from DB first, fall back to localStorage when user logs in
  useEffect(() => {
    if (user?.email) {
      userService.getDivision(user.email)
        .then((res) => {
          const dbDivision = res.data.division;
          if (dbDivision) {
            saveUserDivision(user.email, dbDivision);
            setDivisionState(dbDivision);
          } else {
            const local = getUserDivision(user.email);
            setDivisionState(local);
            if (!local) setDivisionModalOpen(true);
          }
        })
        .catch(() => {
          // Fallback to localStorage if API fails
          const saved = getUserDivision(user.email);
          setDivisionState(saved);
          if (!saved) setDivisionModalOpen(true);
        });
    }
  }, [user?.email]);

  // Check DQ reviewer status
  useEffect(() => {
    if (user?.email) {
      axios.get(api(`/api/dq-reviewers/check/${encodeURIComponent(user.email.toLowerCase())}`))
        .then(res => {
          setIsDQReviewer(res.data.is_dq_reviewer || false);
          setDqDivision(res.data.division || null);
        })
        .catch(() => {});
    }
  }, [user?.email]);

  const value: UserContextType = {
    user,
    isLoading,
    error,
    loadingMessage,
    retryLogin,
    logout,
    isAdmin: false,
    isReviewer: user?.is_reviewer || false,
    isDQReviewer,
    dqDivision,
    division,
    setDivision,
    divisionModalOpen,
    setDivisionModalOpen,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
